import Link from "next/link";
import { useState, type FC } from "react";
import { Button } from "@components/Button";
import type { ShopDetailInfo_ShopFragment } from "@components/ShopDetail/Info/__generated__/fragments";
import { paths } from "@libs/paths";
import { GTMEvent, GTMEvents } from "@libs/utils/gtm";
import { createGoogleMapAppUrl, createGoogleMapUrl } from "@libs/utils/map";
import TelDialog from "../TelDialog";
import styles from "./index.module.css";

const TEL_CELL_LABEL = "電話番号";

type Props = {
  shop: ShopDetailInfo_ShopFragment;
  showsAboutShopStatLink: boolean;
};

const Info: FC<Props> = ({ shop, showsAboutShopStatLink }) => {
  const onSystemPaymentMethods =
    shop.stripeAccount?.isCardPaymentsActive &&
    shop.stripeAccount?.isJcbPaymentsActive
      ? "Visa・Mastercard・JCB・American Express・Diners Club"
      : shop.stripeAccount?.isCardPaymentsActive
      ? "Visa・Mastercard・American Express"
      : shop.stripeAccount?.isJcbPaymentsActive
      ? "JCB・Diners Club"
      : "";

  const [open, setOpen] = useState(false);

  return (
    <section className={styles.information}>
      <h1 className={`${styles.contentTitle} c-contentTitle`}>店舗情報</h1>

      <div className={styles.items}>
        <div className={styles.title}>営業時間</div>
        <div className={styles.cont}>{shop.openingHours}</div>
      </div>
      <div className={styles.items}>
        <div className={styles.title}>定休日</div>
        <div className={styles.cont}>{shop.regularHoliday}</div>
      </div>
      <div className={styles.items}>
        <div className={styles.title}>交通アクセス</div>
        <div className={styles.cont}>
          {shop.closestStation}駅 徒歩{shop.closestStationTime}分 <br />
          {shop.access}
        </div>
      </div>
      <div className={styles.items}>
        <div className={styles.title}>住所</div>
        <div className={styles.cont}>
          {shop.masterPrefecture.name}
          {shop.city}
          {shop.block}
          {shop.building && (
            <>
              <br />
              {shop.building}
            </>
          )}
        </div>
        <div className={styles.map}>
          <iframe
            src={createGoogleMapUrl({
              prefecture: shop.masterPrefecture.name,
              city: shop.city,
              block: shop.block,
              building: shop.building,
              name: shop.name,
            })}
            style={{ border: 0 }}
            allowFullScreen={false}
            aria-hidden="false"
            tabIndex={0}
            loading="lazy"
          />
        </div>
        <div className="md:none">
          <Button
            className={styles.btn}
            styleType="outlined"
            colorType="primary"
            href={createGoogleMapAppUrl({
              prefecture: shop.masterPrefecture.name,
              city: shop.city,
              block: shop.block,
              building: shop.building,
              name: shop.name,
            })}
          >
            <span className={styles.btnLabel}>地図アプリで確認する</span>
          </Button>
        </div>
      </div>
      <div className={styles.items}>
        <div className={styles.title}>{TEL_CELL_LABEL}</div>
        <div className={styles.cont}>
          <a
            className={styles.contTel}
            onClick={() => {
              GTMEvent(GTMEvents.telButtonPressed(TEL_CELL_LABEL, true));
              setOpen(true);
            }}
          >
            {shop.telWithHyphen}
          </a>
        </div>
      </div>
      <div className={styles.items}>
        <div className={styles.title}>予算</div>
        <div className={styles.cont}>{shop.budget}</div>
      </div>
      <div className={styles.items}>
        <div className={styles.title}>支払い方法</div>
        <div className={styles.cont}>
          <div>現地決済：{shop.paymentMethod}</div>
          {onSystemPaymentMethods !== "" && (
            <div>事前決済：クレジットカード({onSystemPaymentMethods})</div>
          )}
        </div>
      </div>
      {shop.chargeFee && (
        <div className={styles.items}>
          <div className={styles.title}>サービス / チャージ料</div>
          <div className={styles.cont}>{shop.chargeFee}</div>
        </div>
      )}
      <div className={styles.items}>
        <div className={styles.title}>座席</div>
        <div className={styles.cont}>{shop.seat}</div>
      </div>
      <div className={styles.items}>
        <div className={styles.title}>モニター</div>
        <div className={styles.cont}>{shop.monitor}</div>
      </div>
      <div className={styles.items}>
        <div className={styles.title}>禁煙 / 喫煙</div>
        <div className={styles.cont}>{shop.smoking}</div>
      </div>
      <div className={styles.items}>
        <div className={styles.title}>駐車場</div>
        <div className={styles.cont}>
          {shop.carPark ? shop.carParkDescription : "なし"}
        </div>
      </div>
      {shop.url && (
        <div className={styles.items}>
          <div className={styles.title}>店舗URL</div>
          <div className={styles.cont}>
            <a href={shop.url} target="_blank">
              {shop.url}
            </a>
          </div>
        </div>
      )}

      <div className={styles.commercialNotice}>
        <Link href={paths.commercialNotice}>※特定商取引法に基づく表記</Link>
        {showsAboutShopStatLink && (
          <Link href={paths.aboutShopStat}>※表示データについて</Link>
        )}
      </div>

      <TelDialog
        fromLabel={TEL_CELL_LABEL}
        uuid={shop.uuid}
        name={shop.name}
        tel={shop.tel}
        telWithHyphen={shop.telWithHyphen}
        canReserve={shop.isReservableOnline}
        open={open}
        onClose={() => setOpen(false)}
      />
    </section>
  );
};

export default Info;
